/**
 * VAD gate AudioWorklet (ADR-0044). Runs the {@link voicingScore}/{@link decideVad} core (from
 * `@duck/contracts`) on the AUDIO THREAD: it watches the filtered mic, opens a smoothed gain gate only on
 * VOICED speech (energy + periodicity), and outputs the gated audio we produce to mediasoup. Because it
 * lives on the audio render thread it (a) never STARVES the way the old main-thread `setInterval` detector
 * did under crowd/render load (the reason that code carries elaborate starvation handling), and (b) keeps
 * the autocorrelation off the main thread, protecting FPS.
 *
 * The processor source is BUILT from the contracts functions via `Function.prototype.toString()` + a Blob,
 * so the audio thread runs the EXACT same code the unit tests exercise — one source of truth. The three
 * core functions are self-contained (no cross-references / module state) precisely so this stringification
 * is sound; see vad.ts.
 */
import { frameRms, voicingScore, decideVad, DEFAULT_VAD_PARAMS, type VadParams } from '@duck/contracts';

const VAD_WORKLET_NAME = 'ddo-vad-gate';

/** Extra (worklet-only) knobs layered on the shared {@link VadParams}. */
export interface VadGateOptions extends VadParams {
  /** Decision frame length (ms). ~25 ms holds ≥2 periods of the lowest tracked pitch (75 Hz). */
  frameMs: number;
  /** Gate OPEN ramp (ms) — fast so a word's first consonant isn't clipped. */
  attackMs: number;
  /** Gate CLOSE ramp (ms) — slow-ish so tails/quiet ends fade smoothly, never choppily. */
  releaseMs: number;
}

const DEFAULT_VAD_GATE_OPTIONS: VadGateOptions = {
  ...DEFAULT_VAD_PARAMS,
  frameMs: 25,
  attackMs: 10,
  releaseMs: 200,
};

/**
 * The AudioWorkletProcessor body, as a string. It references `frameRms`, `voicingScore`, `decideVad` —
 * which we prepend (stringified) when assembling the module — and the worklet globals `sampleRate`,
 * `currentTime`, `AudioWorkletProcessor`, `registerProcessor`.
 */
const PROCESSOR_SHELL = /* js */ `
class VadGateProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = (options && options.processorOptions) || {};
    this.p = o.params;
    this.frameSize = Math.max(256, Math.round(sampleRate * (o.frameMs / 1000)));
    // one-pole ramp coefficients toward the gate target (per sample)
    this.attack = 1 - Math.exp(-1 / Math.max(1, (o.attackMs / 1000) * sampleRate));
    this.release = 1 - Math.exp(-1 / Math.max(1, (o.releaseMs / 1000) * sampleRate));
    this.buf = new Float32Array(this.frameSize);
    this.scratch = new Float32Array(this.frameSize); // reused by voicingScore so it never allocates per frame
    this.fill = 0;
    this.state = { open: false, holdUntil: 0 };
    this.gain = 0;        // current applied gain (ramps toward target)
    this.target = 0;      // 1 when the gate is open, 0 when closed
    this.lastSpeaking = false;
    this.heartbeat = 0;   // frames since the last telemetry post (rms/voicing for the HUD + diagnostics)
    this.muted = false;   // cost-census: when muted (producer paused) we skip the speech-DSP entirely
    this.port.onmessage = (e) => {
      const m = e && e.data;
      if (m && typeof m.muted === 'boolean') this.muted = m.muted;
    };
  }

  process(inputs, outputs) {
    const input = inputs[0] && inputs[0][0];
    const output = outputs[0] && outputs[0][0];
    if (!output) return true;
    if (this.muted) {
      // cost-census: muted ⇒ the producer is paused and nothing is forwarded, so the per-frame
      // autocorrelation (voicingScore) is pure waste — and battery. Output silence and reset the gate so
      // unmute re-opens cleanly on the next voiced frame (the fast attack ramp covers the onset).
      output.fill(0);
      if (this.gain !== 0 || this.fill !== 0 || this.lastSpeaking) {
        this.gain = 0; this.target = 0; this.fill = 0; this.state = { open: false, holdUntil: 0 };
        if (this.lastSpeaking) { this.lastSpeaking = false; this.port.postMessage({ speaking: false, rms: 0, voicing: 0 }); }
      }
      return true;
    }
    if (!input) { output.fill(0); return true; } // no mic frame yet → silence

    for (let i = 0; i < input.length; i++) {
      this.buf[this.fill++] = input[i];
      if (this.fill >= this.frameSize) {
        const rms = frameRms(this.buf);
        const voicing = voicingScore(this.buf, sampleRate, this.scratch); // scratch ⇒ no audio-thread alloc
        const d = decideVad(this.state, rms, voicing, currentTime * 1000, this.p);
        this.state = d;
        this.target = d.open ? 1 : 0;
        // Post on every gate transition (the indicator) AND on a low-rate heartbeat (~every 16 frames,
        // ≈0.4 s) carrying the latest rms/voicing — cheap (a few msgs/s), enough for the meter + debugging.
        if (d.open !== this.lastSpeaking || ++this.heartbeat >= 16) {
          this.lastSpeaking = d.open;
          this.heartbeat = 0;
          this.port.postMessage({ speaking: d.open, rms, voicing });
        }
        this.fill = 0;
      }
      // smooth per-sample ramp toward the target (fast attack, slow release)
      const coeff = this.target > this.gain ? this.attack : this.release;
      this.gain += (this.target - this.gain) * coeff;
      output[i] = input[i] * this.gain;
    }
    return true;
  }
}
registerProcessor(${JSON.stringify(VAD_WORKLET_NAME)}, VadGateProcessor);
`;

/** Built once: the assembled worklet module SOURCE (core functions + processor). The Blob URL is created +
 *  revoked per `addModule` call (below) so no object URL leaks for the page lifetime. */
let moduleSource: string | null = null;
function workletSource(): string {
  if (moduleSource) return moduleSource;
  moduleSource =
    `const frameRms = ${frameRms.toString()};\n` +
    `const voicingScore = ${voicingScore.toString()};\n` +
    `const decideVad = ${decideVad.toString()};\n` +
    PROCESSOR_SHELL;
  return moduleSource;
}

/** Per-AudioContext guard so we only `addModule` once each. */
const loaded = new WeakSet<AudioContext>();

/**
 * Create the VAD gate node for `ctx`, loading the worklet module on first use. Returns the node (an
 * `AudioWorkletNode` whose `.port` emits `{ speaking }` on each gate transition), or `null` when
 * AudioWorklet is unavailable (old browser / insecure context / test env) — the caller then falls back to
 * the legacy main-thread RMS gate, so voice still works, just without the voicing discriminator.
 */
export async function createVadGateNode(
  ctx: AudioContext,
  options?: Partial<VadGateOptions>,
): Promise<AudioWorkletNode | null> {
  if (typeof AudioWorkletNode === 'undefined' || !ctx.audioWorklet) return null;
  const opts: VadGateOptions = { ...DEFAULT_VAD_GATE_OPTIONS, ...options };
  try {
    if (!loaded.has(ctx)) {
      const url = URL.createObjectURL(new Blob([workletSource()], { type: 'application/javascript' }));
      try {
        await ctx.audioWorklet.addModule(url); // the module is fetched + compiled by the time this resolves
      } finally {
        URL.revokeObjectURL(url); // so the object URL never leaks for the page lifetime
      }
      loaded.add(ctx);
    }
    const { frameMs, attackMs, releaseMs, ...params } = opts;
    return new AudioWorkletNode(ctx, VAD_WORKLET_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      processorOptions: { params: params satisfies VadParams, frameMs, attackMs, releaseMs },
    });
  } catch (err) {
    console.warn('[vad-worklet] AudioWorklet load failed — falling back to the RMS gate', err);
    return null;
  }
}
